import React, { useState, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

function formatNum(n) {
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + n;
}

function TrendBadge({ trend }) {
  const colors = {
    BULL: 'bg-green-950 text-green-400 border-green-800',
    BEAR: 'bg-red-950 text-red-400 border-red-800',
    NEUT: 'bg-gray-800 text-gray-400 border-gray-700',
  };
  return <span className={`text-xs px-1.5 py-0.5 rounded border ${colors[trend] || colors.NEUT}`}>{trend || 'NEUT'}</span>;
}

function AgeBadge({ age }) {
  const colors = { Fresh: 'bg-green-950 text-green-400', Aging: 'bg-yellow-950 text-yellow-400', Stale: 'bg-red-950 text-red-400' };
  return <span className={`text-xs px-2 py-0.5 rounded ${colors[age] || colors.Fresh}`}>{age || 'Fresh'}</span>;
}

function SignalCard({ s, onDemoTrade }) {
  const isLong = s.direction === 'LONG';
  const isSolid = s.tier === 'SOLID';
  const rr = isLong ? ((s.tp1 - s.entryHigh) / (s.entryLow - s.sl)).toFixed(1) : ((s.entryLow - s.tp1) / (s.sl - s.entryHigh)).toFixed(1);
  const borderColor = isSolid ? (isLong ? 'border-green-900' : 'border-blue-900') : (isLong ? 'border-orange-900' : 'border-purple-900');
  const bgColor = isSolid ? (isLong ? 'bg-green-950/20' : 'bg-blue-950/20') : (isLong ? 'bg-orange-950/20' : 'bg-purple-950/20');
  return (
    <div className={`border ${borderColor} ${bgColor} rounded-xl p-4 mb-4`}>
      {!isSolid && <div className="bg-red-950 border border-red-800 rounded-lg px-3 py-2 text-xs text-red-400 mb-3">Risky signal — higher failure probability. TP1 only. Trade at your own risk.</div>}
      {(s.warnings || []).map((w, i) => <div key={i} className="bg-yellow-950 border border-yellow-800 rounded-lg px-3 py-1.5 text-xs text-yellow-400 mb-2">{w}</div>)}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base font-bold text-white">{s.symbol.replace('USDT', '')}<span className="text-gray-500 font-normal text-sm">/USDT</span></span>
          <span className={`text-xs px-2 py-0.5 rounded font-medium border ${isLong ? 'bg-green-950 text-green-400 border-green-800' : 'bg-red-950 text-red-400 border-red-800'}`}>{isLong ? 'LONG' : 'SHORT'}</span>
          <span className={`text-xs px-2 py-0.5 rounded border ${isSolid ? 'bg-yellow-950 text-yellow-400 border-yellow-800' : 'bg-red-950/50 text-red-400 border-red-800'}`}>{s.tier}</span>
          <AgeBadge age={s.age} />
        </div>
        <div className="text-right">
          <div className={`text-xl font-bold ${isSolid ? 'text-green-400' : 'text-red-400'}`}>{s.confidence}%</div>
          <div className="text-xs text-gray-500">Confidence</div>
          <div className="w-14 h-1 bg-gray-800 rounded-full mt-1"><div className="h-1 rounded-full" style={{ width: s.confidence + '%', background: isSolid ? '#3fb950' : '#e8496a' }}></div></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-black/30 rounded-lg p-2"><div className="text-xs text-gray-500 mb-0.5">Entry range</div><div className="text-xs font-medium text-blue-400">{s.entryLow} — {s.entryHigh}</div></div>
        <div className="bg-black/30 rounded-lg p-2"><div className="text-xs text-gray-500 mb-0.5">Stop loss</div><div className="text-xs font-medium text-red-400">{s.sl}</div></div>
        <div className="bg-black/30 rounded-lg p-2"><div className="text-xs text-gray-500 mb-0.5">Take profit</div><div className="text-xs font-medium text-green-400">TP1: {s.tp1}{s.tp2 ? ` · TP2: ${s.tp2}` : ''}</div></div>
      </div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-1.5"><span className="text-xs text-gray-500">R:R</span><span className="text-xs px-2 py-0.5 bg-blue-950 text-blue-400 border border-blue-800 rounded">{rr}:1</span></div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">Prob(win)</span>
          <div className="w-16 h-1 bg-gray-800 rounded-full"><div className="h-1 rounded-full" style={{ width: (s.probWin || 0) + '%', background: s.probWin > 70 ? '#3fb950' : '#d4a017' }}></div></div>
          <span className="text-xs font-medium" style={{ color: s.probWin > 70 ? '#3fb950' : '#d4a017' }}>{s.probWin}%</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        <span className="text-xs text-gray-500">Trend:</span>
        <span className="text-xs text-gray-600">5m</span><TrendBadge trend={s.trend5m} />
        <span className="text-xs text-gray-600">15m</span><TrendBadge trend={s.trend15m} />
        <span className="text-xs text-gray-600">1h</span><TrendBadge trend={s.trend1h} />
        <span className="text-xs text-gray-600">4h</span><TrendBadge trend={s.trend4h} />
      </div>
      <div className="grid grid-cols-4 gap-1.5 mb-3">
        {[
          { label: 'RSI', value: s.rsi, color: s.rsi > 70 ? '#f85149' : s.rsi < 30 ? '#3fb950' : '#e6edf3' },
          { label: 'Funding', value: ((s.funding || 0) * 100).toFixed(4) + '%', color: Math.abs(s.funding || 0) > 0.01 ? '#f85149' : '#8b949e' },
          { label: 'OI Delta', value: ((s.oiDelta || 0) > 0 ? '+' : '') + (s.oiDelta || 0) + '%', color: (s.oiDelta || 0) > 0 ? '#3fb950' : '#f85149' },
          { label: 'Regime', value: s.regime, color: s.regime === 'TRENDING' ? '#3fb950' : s.regime === 'VOLATILE' ? '#f85149' : '#d4a017' },
          { label: 'Whale In', value: formatNum(s.whaleIn || 0), color: '#3fb950' },
          { label: 'Whale Out', value: formatNum(s.whaleOut || 0), color: '#f85149' },
          { label: 'Liq Buy', value: formatNum(s.liqBuy || 0), color: '#3fb950' },
          { label: 'Liq Sell', value: formatNum(s.liqSell || 0), color: '#f85149' },
        ].map((m, i) => (
          <div key={i} className="bg-black/25 rounded p-1.5">
            <div className="text-gray-600 mb-0.5" style={{ fontSize: '10px' }}>{m.label}</div>
            <div className="font-medium" style={{ color: m.color, fontSize: '10px' }}>{m.value}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-xs text-gray-500">RVOL:</span>
        {[['5m', s.rvol5m], ['15m', s.rvol15m], ['1h', s.rvol1h], ['4h', s.rvol4h]].map(([t, v]) => (
          <span key={t} className="text-xs bg-black/30 px-1.5 py-0.5 rounded text-gray-400">{t} {v || '0x'}</span>
        ))}
      </div>
      {s.kg && <div className="bg-yellow-950/30 border border-yellow-900/50 rounded-lg px-3 py-2 text-xs text-yellow-500 mb-3">{s.kg}</div>}
      <div className="flex items-center justify-between pt-3 border-t border-white/5 flex-wrap gap-2">
        <span className="text-xs text-gray-500">Size: <span className={`font-medium ${(s.suggestedSize || 1) >= 1 ? 'text-green-400' : 'text-yellow-400'}`}>{s.suggestedSize || 1}x</span></span>
        <div className="flex items-center gap-2">
          <button onClick={() => onDemoTrade(s)} className="text-xs px-3 py-1.5 bg-orange-950 border border-orange-800 text-orange-400 rounded-lg hover:bg-orange-900 transition-all">Demo Trade</button>
          <a href={"https://www.binance.com/en/futures/" + s.symbol} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 bg-yellow-950 border border-yellow-800 text-yellow-400 rounded-lg hover:bg-yellow-900 transition-all">Binance Chart</a>
        </div>
      </div>
    </div>
  );
}

function DemoTab({ initialSignal }) {
  const [balance, setBalance] = useState(() => {
    const saved = localStorage.getItem('demo_balance');
    return saved ? parseFloat(saved) : 100000;
  });
  const [positions, setPositions] = useState(() => {
    const saved = localStorage.getItem('demo_positions');
    return saved ? JSON.parse(saved) : [];
  });
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem('demo_history');
    return saved ? JSON.parse(saved) : [];
  });
  const [wins, setWins] = useState(() => {
    const saved = localStorage.getItem('demo_wins');
    return saved ? parseInt(saved) : 0;
  });
  const [sig, setSig] = useState(initialSignal);
  const [entry, setEntry] = useState(initialSignal ? String(initialSignal.entryLow) : '');
  const [sl, setSl] = useState(initialSignal ? String(initialSignal.sl) : '');
  const [tp, setTp] = useState(initialSignal ? String(initialSignal.tp1) : '');
  const [size, setSize] = useState('1000');
  const [lev, setLev] = useState(10);
  const [customLev, setCustomLev] = useState('');
  const [orderType, setOrderType] = useState('Market');

  useEffect(() => {
    localStorage.setItem('demo_balance', balance);
  }, [balance]);

  useEffect(() => {
    localStorage.setItem('demo_positions', JSON.stringify(positions));
  }, [positions]);

  useEffect(() => {
    localStorage.setItem('demo_history', JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    localStorage.setItem('demo_wins', wins);
  }, [wins]);

  useEffect(() => {
    if (initialSignal) { setSig(initialSignal); setEntry(String(initialSignal.entryLow)); setSl(String(initialSignal.sl)); setTp(String(initialSignal.tp1)); }
  }, [initialSignal]);

  const entryN = parseFloat(entry) || 0;
  const slN = parseFloat(sl) || 0;
  const tpN = parseFloat(tp) || 0;
  const sizeN = parseFloat(size) || 0;
  const margin = sizeN / lev;
  const isLong = sig ? sig.direction === 'LONG' : true;
  const liqPrice = isLong ? (entryN * (1 - 1/lev)) : (entryN * (1 + 1/lev));
  const maxProfit = isLong ? ((tpN-entryN)/entryN*sizeN) : ((entryN-tpN)/entryN*sizeN);
  const maxLoss = Math.min(sizeN, isLong ? ((entryN-slN)/entryN*sizeN) : ((slN-entryN)/entryN*sizeN));
  const rr = Math.abs(tpN-entryN)/Math.abs(entryN-slN);
  const totalPnl = history.reduce((a,t) => a+t.pnl, 0);
  const winRate = history.length ? Math.round(wins/history.length*100) : 0;
  const liqWarnClass = lev<=20 ? 'bg-gray-900 border-gray-700 text-gray-400' : lev<=100 ? 'bg-yellow-950 border-yellow-800 text-yellow-400' : lev<=500 ? 'bg-red-950 border-red-800 text-red-400' : 'bg-purple-950 border-purple-800 text-purple-400';
  const liqWarnText = lev<=20 ? `Liq price: $${liqPrice.toFixed(2)}` : lev<=100 ? `High leverage — Liq: $${liqPrice.toFixed(2)}` : lev<=500 ? `Extreme leverage — Liq: $${liqPrice.toFixed(2)}` : `DEGEN MODE ${lev}x — Liq: $${liqPrice.toFixed(4)} (demo only!)`;
  const levOptions = [2,5,10,20,50,100,500,1000,3000];

  const openTrade = () => {
    if (!sig) return;
    if (sizeN > balance) return alert('Not enough demo balance!');
    const pos = Math.random() > 0.45;
    const pnl = parseFloat((sizeN*(Math.random()*8+0.5)/100*(pos?1:-1)).toFixed(2));
    setBalance(b => parseFloat((b+pnl).toFixed(2)));
    if (pos) setWins(w => w+1);
    const trade = { sym: sig.symbol, dir: sig.direction, tier: sig.tier, size: sizeN, lev, entry: entryN, sl: slN, tp: tpN, pnl, pos, time: 'just now' };
    setPositions(p => [trade,...p]);
    setHistory(h => [{...trade},...h]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div><div className="text-sm font-bold text-orange-400">Demo Trading</div><div className="text-xs text-gray-500">$100,000 USDT paper balance — no real money</div></div>
        <button onClick={() => { if(window.confirm('Reset?')) { setBalance(100000); setPositions([]); setHistory([]); setWins(0); setSig(null); }}} className="text-xs px-3 py-1.5 bg-red-950 border border-red-800 text-red-400 rounded-lg">Reset</button>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[{ label:'Balance', value:'$'+Math.round(balance).toLocaleString(), color:'text-orange-400' },{ label:'Total P/L', value:(totalPnl>=0?'+':'')+' $'+Math.abs(totalPnl).toFixed(0), color:totalPnl>=0?'text-green-400':'text-red-400' },{ label:'Win rate', value:history.length?winRate+'%':'—', color:'text-white' },{ label:'Trades', value:history.length, color:'text-white' }].map((s,i) => (
          <div key={i} className="bg-gray-900 border border-orange-900/50 rounded-lg p-2.5"><div className="text-xs text-gray-500 mb-1">{s.label}</div><div className={`text-sm font-bold ${s.color}`}>{s.value}</div></div>
        ))}
      </div>
      {sig ? (
        <div className="bg-gray-900 border border-orange-800/50 rounded-xl p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-orange-400">Demo Trade — <span className="text-white">{sig.symbol} {sig.direction}</span></div>
            <button onClick={() => setSig(null)} className="text-gray-600 hover:text-white text-lg">✕</button>
          </div>
          <div className="flex gap-2 mb-4">
            {['Market','Limit','Stop-Limit'].map(t => <button key={t} onClick={() => setOrderType(t)} className={`flex-1 text-xs py-1.5 rounded-lg border transition-all ${orderType===t?'border-orange-600 text-orange-400 bg-orange-950':'border-gray-700 text-gray-500'}`}>{t}</button>)}
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {[['Entry price',entry,setEntry,`Range: ${sig.entryLow}—${sig.entryHigh}`],['Stop loss',sl,setSl,`Signal SL: ${sig.sl}`],['Take profit',tp,setTp,`TP1: ${sig.tp1}`]].map(([label,val,setter,hint]) => (
              <div key={label}><label className="text-xs text-gray-500 mb-1 block">{label}</label><input value={val} onChange={e => setter(e.target.value)} className="w-full bg-gray-950 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white outline-none focus:border-orange-500"/><div className="text-xs text-gray-700 mt-0.5">{hint}</div></div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div><label className="text-xs text-gray-500 mb-1 block">Position size (USDT)</label><input value={size} onChange={e => setSize(e.target.value)} className="w-full bg-gray-950 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-white outline-none focus:border-orange-500"/></div>
            <div><label className="text-xs text-gray-500 mb-1 block">Demo balance</label><div className="bg-gray-950 border border-gray-700 rounded-lg px-2 py-1.5 text-xs text-orange-400">${Math.round(balance).toLocaleString()}</div></div>
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-500 mb-2 block">Leverage</label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {levOptions.map(l => <button key={l} onClick={() => { setLev(l); setCustomLev(''); }} className={`text-xs px-2 py-1 rounded border transition-all ${lev===l&&!customLev?'border-orange-600 text-orange-400 bg-orange-950':'border-gray-700 text-gray-500'}`}>{l}x</button>)}
              <input value={customLev} onChange={e => { setCustomLev(e.target.value); setLev(parseInt(e.target.value)||10); }} placeholder="Custom" className="w-16 bg-gray-950 border border-gray-700 rounded px-2 py-1 text-xs text-white outline-none focus:border-orange-500"/>
            </div>
          </div>
          <div className={`border rounded-lg px-3 py-2 text-xs mb-3 ${liqWarnClass}`}>{liqWarnText}</div>
          <div className="bg-gray-950 rounded-lg p-3 mb-3 grid grid-cols-2 gap-1.5">
            {[['Margin required','$'+margin.toFixed(2)],['R:R ratio',isNaN(rr)?'--':rr.toFixed(1)+':1'],['Max profit','+$'+Math.max(0,maxProfit).toFixed(2)],['Max loss','-$'+Math.min(sizeN,Math.max(0,maxLoss)).toFixed(2)]].map(([l,v]) => (
              <div key={l} className="flex justify-between"><span className="text-xs text-gray-500">{l}</span><span className="text-xs font-medium text-white">{v}</span></div>
            ))}
          </div>
          <button onClick={openTrade} disabled={sizeN>balance} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg text-sm transition-all">{sizeN>balance?'Not enough demo balance':'Open Demo Trade'}</button>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-4 text-center"><div className="text-xs text-gray-500">No signal selected. Go to Signals tab and click Demo Trade on any signal.</div></div>
      )}
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Open Positions</div>
      {positions.length===0 ? <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center text-xs text-gray-500 mb-4">No open positions</div> : positions.map((p,i) => (
        <div key={i} className="bg-gray-900 border border-orange-800/50 rounded-xl p-3 mb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><span className="text-sm font-bold text-white">{p.sym.replace('USDT','')}</span><span className={`text-xs px-1.5 py-0.5 rounded ${p.dir==='LONG'?'bg-green-950 text-green-400':'bg-red-950 text-red-400'}`}>{p.dir}</span><span className="text-xs text-orange-400">{p.lev}x</span></div>
            <span className={`text-sm font-bold ${p.pnl>=0?'text-green-400':'text-red-400'}`}>{p.pnl>=0?'+':''}${Math.abs(p.pnl).toFixed(0)}</span>
          </div>
          <div className="flex gap-3 text-xs text-gray-500 mb-2 flex-wrap"><span>Size: ${p.size.toLocaleString()}</span><span>Entry: ${p.entry}</span><span>SL: <span className="text-red-400">${p.sl}</span></span><span>TP: <span className="text-green-400">${p.tp}</span></span></div>
          <button onClick={() => setPositions(pos => pos.filter((_,idx) => idx!==i))} className="text-xs px-3 py-1 bg-red-950 border border-red-800 text-red-400 rounded-lg">Close position</button>
        </div>
      ))}
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2 mt-4">Demo History</div>
      {history.length===0 ? <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 text-center text-xs text-gray-500">No trades yet</div> : history.map((h,i) => (
        <div key={i} className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 mb-1.5">
          <span className="text-xs font-medium w-12">{h.sym.replace('USDT','')}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded w-10 text-center ${h.dir==='LONG'?'bg-green-950 text-green-400':'bg-red-950 text-red-400'}`}>{h.dir}</span>
          <span className="text-xs text-orange-400 w-10">{h.lev}x</span>
          <span className="flex-1 text-xs text-gray-500">${h.size.toLocaleString()}</span>
          <span className={`text-xs font-medium ${h.pnl>=0?'text-green-400':'text-red-400'}`}>{h.pnl>=0?'+':''}${Math.abs(h.pnl).toFixed(0)}</span>
          <span className="text-xs text-gray-600">{h.time}</span>
        </div>
      ))}
    </div>
  );
}

function PREP({ onLogout }) {
  const [activeTab, setActiveTab] = useState('signals');
  const [filter, setFilter] = useState('all');
  const [countdown, setCountdown] = useState(0);
  const [demoSignal, setDemoSignal] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanStatus, setScanStatus] = useState({ scanned: 0, signals_found: 0, cycle_time: 0 });
  const [marketData, setMarketData] = useState({ btc: { price: '—', change: '0' }, eth: { price: '—', change: '0' }, gainers: [], losers: [], funding: [] });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'prep_signals'), (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data(), age: getAge(doc.data().createdAt) }));
      setSignals(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'engine_status'), (snap) => {
      snap.docs.forEach(doc => {
        if (doc.id === 'scan') {
          const data = doc.data();
          setScanStatus(data);
          if (data.last_scan && data.cycle_time) {
            const lastMs = data.last_scan.toMillis ? data.last_scan.toMillis() : Date.now();
            const nextMs = lastMs + (data.cycle_time * 1000) + (10 * 60 * 1000);
            setCountdown(Math.max(0, Math.floor((nextMs - Date.now()) / 1000)));
          }
        }
      });
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'market_data'), (snap) => {
      snap.docs.forEach(doc => {
        if (doc.id === 'live') {
          const data = doc.data();
          setMarketData({
            btc: data.btc || { price: '—', change: '0' },
            eth: data.eth || { price: '—', change: '0' },
            gainers: (data.gainers || []).map(g => [g.symbol, g.change]),
            losers: (data.losers || []).map(g => [g.symbol, g.change]),
            funding: (data.funding || []).map(f => [f.symbol, f.rate, f.positive])
          });
        }
      });
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCountdown(c => c <= 0 ? 0 : c - 1), 1000);
    return () => clearInterval(timer);
  }, []);

  function getAge(createdAt) {
    if (!createdAt) return 'Fresh';
    const ms = typeof createdAt === 'number' ? createdAt : createdAt.toMillis ? createdAt.toMillis() : Date.now();
    const mins = (Date.now() - ms) / 60000;
    if (mins < 10) return 'Fresh';
    if (mins < 25) return 'Aging';
    return 'Stale';
  }

  const formatCD = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec < 10 ? '0' : ''}${sec}`;
  };

  const filtered = signals.filter(s => {
    if (filter === 'solid') return s.tier === 'SOLID';
    if (filter === 'risky') return s.tier === 'RISKY';
    if (filter === 'long') return s.direction === 'LONG';
    if (filter === 'short') return s.direction === 'SHORT';
    return true;
  });

  const tabs = ['signals', 'market', 'history', 'demo'];

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      <div className="bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <svg width="20" height="20" viewBox="0 0 100 130" fill="none">
            <defs><linearGradient id="tg" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#f2c832"/><stop offset="100%" stopColor="#e8496a"/></linearGradient></defs>
            <path d="M58 4L84 4L84 10L68 10L68 54L84 54L84 62L52 126L42 126L62 72L44 72L44 62L16 62L16 54L32 54L32 10L16 10L16 4L42 4L42 54L58 54Z" fill="url(#tg)"/>
          </svg>
          <span className="font-bold text-sm bg-gradient-to-r from-yellow-400 to-pink-500 bg-clip-text text-transparent">ArcBolt</span>
          <span className="text-xs px-2 py-0.5 bg-yellow-950 text-yellow-400 border border-yellow-800 rounded ml-1">PREP</span>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t} onClick={() => setActiveTab(t)} className={`text-xs px-3 py-1.5 rounded-lg capitalize transition-all whitespace-nowrap ${activeTab===t ? t==='demo'?'bg-orange-950 text-orange-400 border border-orange-800':'bg-gray-800 text-white' : t==='demo'?'text-orange-400':'text-gray-500 hover:text-white'}`}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
            <span className="text-xs text-gray-500">Next: {formatCD(countdown)}</span>
          </div>
          <button onClick={onLogout} className="text-gray-500 hover:text-red-400 transition-all"><LogOut size={16}/></button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'signals' && (
          <div>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {[{ label:'Total', value:signals.length, color:'text-white' },{ label:'Solid', value:signals.filter(s=>s.tier==='SOLID').length, color:'text-green-400' },{ label:'Risky', value:signals.filter(s=>s.tier==='RISKY').length, color:'text-red-400' },{ label:'Next scan', value:formatCD(countdown), color:'text-gray-400' }].map((s,i) => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-lg p-2.5"><div className="text-xs text-gray-500 mb-1">{s.label}</div><div className={`text-base font-bold ${s.color}`}>{s.value}</div></div>
              ))}
            </div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs text-gray-500">Filter:</span>
              {['all','solid','risky','long','short'].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1 rounded-full border transition-all capitalize ${filter===f ? f==='solid'?'border-green-600 text-green-400 bg-green-950':f==='risky'?'border-red-600 text-red-400 bg-red-950':'border-blue-600 text-blue-400 bg-blue-950' : 'border-gray-700 text-gray-500 hover:border-gray-500'}`}>{f}</button>
              ))}
              <span className="text-xs text-gray-700 ml-auto">Engine v3.16.0</span>
            </div>
            {loading ? <div className="text-center py-10 text-gray-500 text-sm">Loading signals...</div>
              : filtered.length===0 ? <div className="text-center py-10"><div className="text-gray-500 text-sm mb-2">No signals available</div><div className="text-gray-700 text-xs">Engine scanning — signals appear when confidence threshold is met</div></div>
              : filtered.map(s => <SignalCard key={s.id} s={s} onDemoTrade={(sig) => { setDemoSignal(sig); setActiveTab('demo'); }} />)}
          </div>
        )}

        {activeTab === 'market' && (
          <div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { label:'BTC/USDT', value:'$'+marketData.btc.price, change:(parseFloat(marketData.btc.change)>=0?'+':'')+marketData.btc.change+'%', pos:parseFloat(marketData.btc.change)>=0 },
                { label:'ETH/USDT', value:'$'+marketData.eth.price, change:(parseFloat(marketData.eth.change)>=0?'+':'')+marketData.eth.change+'%', pos:parseFloat(marketData.eth.change)>=0 },
                { label:'BTC Dominance', value:'54.2%', change:'MCap: $2.41T', pos:true },
                { label:'Engine Status', value:scanStatus.status||'—', change:'v3.16.0', pos:true },
              ].map((m,i) => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-3"><div className="text-xs text-gray-500 mb-1">{m.label}</div><div className="text-base font-bold text-blue-400">{m.value}</div><div className={`text-xs mt-0.5 ${m.pos?'text-green-400':'text-red-400'}`}>{m.change}</div></div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-3">
                <div className="text-xs text-gray-500 mb-2">Top Gainers 24h</div>
                {marketData.gainers.length===0 ? <div className="text-xs text-gray-600">Loading...</div> : marketData.gainers.map(([s,c]) => (
                  <div key={s} className="flex justify-between py-1 border-b border-gray-800 last:border-0"><span className="text-xs">{s}</span><span className="text-xs text-green-400 font-medium">{c}</span></div>
                ))}
              </div>
              <div className="bg-gray-900 border border-gray-800 rounded-xl p-3">
                <div className="text-xs text-gray-500 mb-2">Top Losers 24h</div>
                {marketData.losers.length===0 ? <div className="text-xs text-gray-600">Loading...</div> : marketData.losers.map(([s,c]) => (
                  <div key={s} className="flex justify-between py-1 border-b border-gray-800 last:border-0"><span className="text-xs">{s}</span><span className="text-xs text-red-400 font-medium">{c}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-3 mb-4">
              <div className="text-xs text-gray-500 mb-2">Funding Rates</div>
              <div className="grid grid-cols-2 gap-2">
                {marketData.funding.length===0 ? <div className="text-xs text-gray-600">Loading...</div> : marketData.funding.map(([s,f,ok]) => (
                  <div key={s} className="flex justify-between bg-gray-950 rounded px-2 py-1.5"><span className="text-xs">{s}</span><span className={`text-xs font-medium ${ok?'text-green-400':'text-red-400'}`}>{f}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-3">
              <div className="text-xs text-gray-500 mb-2">Scan Activity</div>
              <div className="grid grid-cols-4 gap-2 text-center">
                {[[scanStatus.scanned||'—','Scanned'],[scanStatus.signals_found||'—','Signals'],[(scanStatus.scanned||0)-(scanStatus.signals_found||0)||'—','Skipped'],[scanStatus.status||'—','Status']].map(([v,l]) => (
                  <div key={l}><div className="text-base font-bold text-white">{v}</div><div className="text-xs text-gray-500">{l}</div></div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'history' && (
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Signal History</div>
            <div className="text-center py-8 text-gray-600 text-xs">History will populate as signals are generated</div>
          </div>
        )}

        {activeTab === 'demo' && <DemoTab initialSignal={demoSignal} />}
      </div>

      <div className="bg-gray-900 border-t border-gray-800 px-4 py-2 text-center">
        <p className="text-xs text-gray-700">Not financial advice · Signals informational only · Trading involves risk of total loss</p>
      </div>
    </div>
  );
}

export default PREP;